import type { EChartsOption } from 'echarts';
import type { TooltipComponentFormatterCallbackParams } from 'echarts';
import type { ParsedTable, ChartGeneratorSettings } from '../types';
import {
  toNumber,
  axisPercentFormatter,
  resolveSeriesColors,
  GeneratorError,
  getChartColorByIndex,
  chartLegend,
  CHART_GRID_TOP_WITH_LEGEND,
  CHART_GRID_BOTTOM,
  uniqueCategoryNames,
  cleanCategoryName,
} from './helpers';
import { filterTableData } from '../utils/chartFilters';
import { sortTableForChart } from '../utils/chartSort';

/**
 * Generates ECharts options for a horizontal Stacked Bar chart
 *
 * @param t - The parsed table data
 * @param title - The title for the chart
 * @param settings - Settings (selectedRows, selectedColumns, dataType)
 * @returns ECharts option configuration object for a stacked bar chart
 * @throws {GeneratorError} If filtering removes all data
 */
export function stackedBarOptions(
  t: ParsedTable,
  title: string,
  settings?: ChartGeneratorSettings
): EChartsOption {
  // Positions win over names (#1808); a set sort_order puts the bars in
  // reading order (drawn top-down by `yAxis.inverse`). Absent, table order as before.
  const sortOrder = settings?.sortOrder
  const pickedTable = filterTableData(
    t,
    settings?.selectedRows,
    settings?.selectedColumns,
    settings?.selectedRowPositions,
  )
  const filteredTable = sortOrder ? sortTableForChart(pickedTable, 'stackedBar', sortOrder) : pickedTable

  const categories = filteredTable.headers.slice(1) // Series names (columns)
  // Repeated labels stay separate bars (#1808); unique ones are unchanged.
  const yAxisData = uniqueCategoryNames(filteredTable.rows.map((row) => row[0] || 'Unnamed'))

  const gradientMap = settings?.gradientMap

  const series: echarts.BarSeriesOption[] = categories.map((category, catIndex) => {
    const data = filteredTable.rows.map((row) => {
      const dataIndex = catIndex + 1
      return dataIndex < row.length ? toNumber(row[dataIndex]) : null
    })

    // Look up gradient or color for this series based on category name
    const key = category.toLowerCase();
    const gradient = gradientMap?.[key];
    const color = settings?.colorMap?.[key] || getChartColorByIndex(catIndex);

    return {
      name: category,
      type: 'bar',
      stack: 'total', // Enable stacking
      data: data,
      emphasis: {
        focus: 'series',
      },
      itemStyle: { color: gradient || color },
    }
  })

  const hasData = series.some((s) => s.data?.some((d) => d !== null && !isNaN(d as number)))
  if (!hasData) {
    throw new GeneratorError('No valid numeric data found after filtering.')
  }

  return {
    title: {
      text: title,
      left: 'left',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: (params: TooltipComponentFormatterCallbackParams) => {
        if (!Array.isArray(params)) return ''
        let tooltipText = `${cleanCategoryName(params[0].name)}<br/>`
        params.forEach((item) => {
          const value = typeof item.value === 'number' ? item.value : null
          const formattedValue =
            value !== null
              ? settings?.dataType === 'freq'
                ? value.toLocaleString()
                : `${(value * 100).toFixed(1)}%`
              : 'N/A'
          const color = (item.color as string) || '#ccc'
          tooltipText += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>${item.seriesName}: ${formattedValue}<br/>`
        })
        return tooltipText
      },
    },
    legend: chartLegend(categories),
    grid: {
      left: '3%',
      right: '4%',
      top: CHART_GRID_TOP_WITH_LEGEND,
      bottom: CHART_GRID_BOTTOM,
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      axisLabel: {
        formatter: settings?.dataType === 'freq' ? undefined : axisPercentFormatter,
      },
    },
    yAxis: {
      type: 'category',
      data: yAxisData,
      ...(sortOrder ? { inverse: true } : {}),
      axisLabel: {
        // interval deliberately unset (#1178): ECharts' own 'auto' thins
        // colliding labels — 25 months no longer smear into one band. The
        // old force-every-label look is the "Every label" setting.
        fontSize: 10,
      },
    },
    series: series,
    color: resolveSeriesColors(categories, settings?.colorMap),
  }
}
